import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Bell, X as XIcon, CheckCheck, Trash2,
  AlertTriangle, CheckCircle2, Pause, Info, Check, Briefcase, MessageSquare, ChevronRight,
} from 'lucide-react';

const API = `${window.__BACKEND_URL__}/api`;
const POLL_MS = 30000;

const ICON_BY_TYPE = {
  smart_alert: AlertTriangle,
  job_pause: Pause,
  job_resume: CheckCircle2,
  qb_error: AlertTriangle,
  job_assigned: Briefcase,
  job_reassigned: Briefcase,
  job_note: MessageSquare,
};

// Notification types that show the one-tap "Acknowledge" button.
const ACK_TYPES = new Set(['job_assigned', 'job_reassigned']);

const COLOR_BY_SEVERITY = {
  success: '#00C896',
  warning: '#FFA000',
  error: '#EF4444',
  info: '#1E88E5',
};

function timeAgo(iso) {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  const diff = Math.max(0, Date.now() - then);
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(iso).toLocaleDateString();
}

function resolveLink(n) {
  if (n.link) return n.link;
  const meta = n.meta || {};
  const jobId = meta.job_id;
  const userId = meta.user_id;

  switch (n.type) {
    case 'job_assigned':
    case 'job_reassigned':
    case 'job_note':
    case 'job_pause':
    case 'job_resume':
    case 'job_completed':
    case 'job_updated':
      return jobId ? `/jobs?job=${jobId}` : '/jobs';

    case 'time_submitted':
    case 'time_returned':
    case 'time_approved':
    case 'time_amendment':
    case 'amendment_approved':
    case 'amendment_denied':
    case 'supervisor_approval_required':
    case 'pending_approvals':
      return '/reports';

    case 'payroll_ready':
    case 'payroll_exception':
    case 'payroll_locked':
      return '/reports';

    case 'ot_warning':
    case 'ot_warning_75':
    case 'ot_warning_90':
      return userId ? `/reports?user_id=${userId}&preset=this_week` : '/reports';

    case 'missed_punch':
    case 'squad_missed_punch':
      return userId ? `/reports?user_id=${userId}&preset=today` : '/reports';

    case 'smart_alert':
      return jobId ? `/jobs?job=${jobId}` : '/jobs';

    case 'safety_announcement':
    case 'toolbox_reminder':
      return '/home';

    default:
      return null;
  }
}

export default function NotificationBell() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(false);
  const popoverRef = useRef(null);
  const buttonRef = useRef(null);
  const pollRef = useRef(null);
  // Snapshot of notification ids we've already shown a toast for. Persists
  // across re-renders but not across reloads — that's intentional, so a fresh
  // tab doesn't spam toasts for historical notes.
  const toastedIdsRef = useRef(new Set());
  // We seed the snapshot from the FIRST successful fetch so we don't toast
  // every existing notification when the user lands on a new page.
  const seededRef = useRef(false);
  // markOne handler exposed for the toast action callback. Assigned below
  // once markOne is defined — refs are stable across renders.
  const markOneRef = useRef(null);

  // Cookie sessions (Jun 2026): JWT lives in an httpOnly cookie on the web —
  // localStorage only has a token in the native shell. Send Bearer only when
  // present; otherwise the cookie authenticates the request.
  const token = () => (typeof window !== 'undefined' ? localStorage.getItem('token') : null);
  const authOpts = () => {
    const t = token();
    return t ? { headers: { Authorization: `Bearer ${t}` } } : {};
  };

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/notifications`, authOpts());
      const next = r.data?.notifications || [];

      // Real-time toast for newly-arrived job_note (and other interactive)
      // notifications between polls. Suppress on the very first fetch so we
      // don't spam the user on page load.
      if (!seededRef.current) {
        next.forEach((n) => toastedIdsRef.current.add(n.id));
        seededRef.current = true;
      } else {
        const fresh = next.filter(
          (n) => !n.read && !toastedIdsRef.current.has(n.id),
        );
        // Toast only "interactive" types (notes + assignment + alerts) so we
        // don't surprise users with pop-ups for routine system events.
        const TOASTABLE = new Set(['job_note', 'job_assigned', 'job_reassigned', 'smart_alert']);
        fresh.filter((n) => TOASTABLE.has(n.type)).forEach((n) => {
          const Icon = ICON_BY_TYPE[n.type] || Bell;
          toast(n.title || 'New notification', {
            description: n.body || n.message || '',
            icon: <Icon className="w-4 h-4" />,
            action: n.link
              ? {
                  label: 'Open',
                  onClick: () => {
                    // Mark read so the badge doesn't double-decrement
                    markOneRef.current?.(n.id);
                    // Navigate inside the SPA when the link is relative
                    if (n.link.startsWith('/')) {
                      navigate(n.link);
                    } else {
                      window.location.href = n.link;
                    }
                  },
                }
              : undefined,
            duration: 8000,
          });
        });
        fresh.forEach((n) => toastedIdsRef.current.add(n.id));
      }

      setItems(next);
      setUnread(r.data?.unread_count || 0);
    } catch (_) {
      // silent — authentication flow handles token expiry
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    fetchItems();
    pollRef.current = setInterval(fetchItems, POLL_MS);
    return () => clearInterval(pollRef.current);
  }, [fetchItems]);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target)
          && buttonRef.current && !buttonRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const markOne = async (id) => {
    try {
      await axios.post(`${API}/notifications/${id}/read`, {},
        authOpts());
      fetchItems();
    } catch (_) { /* ignore */ }
  };

  // Keep markOneRef current so the toast action can fire even after the
  // component re-renders.
  useEffect(() => { markOneRef.current = markOne; });

  const markAll = async () => {
    try {
      await axios.post(`${API}/notifications/read-all`, {},
        authOpts());
      fetchItems();
    } catch (_) { /* ignore */ }
  };

  const del = async (id) => {
    try {
      await axios.delete(`${API}/notifications/${id}`,
        authOpts());
      fetchItems();
    } catch (_) { /* ignore */ }
  };

  // One-tap acknowledgement for job assignment notifications. Posts to the
  // backend, which stamps the job + marks every related notification read.
  const [ackingId, setAckingId] = useState(null);
  const acknowledgeAssignment = async (n) => {
    const jobId = n?.meta?.job_id;
    if (!jobId) return;
    setAckingId(n.id);
    try {
      await axios.post(
        `${API}/jobs/${jobId}/acknowledge-assignment`,
        {},
        authOpts()
      );
      // Mark THIS notification read locally so the green flash is instant,
      // then refresh from the server (the backend also marks every related
      // job_assigned/job_reassigned notification for this user+job as read).
      setItems((prev) => prev.map((it) => (it.id === n.id ? { ...it, read: true, acknowledged: true } : it)));
      setUnread((u) => Math.max(0, u - 1));
      fetchItems();
    } catch (_) {
      /* ignore — keep the button in the failed state will let the user retry */
    } finally {
      setAckingId(null);
    }
  };

  const onItemClick = async (n) => {
    if (!n.read) await markOne(n.id);
    const dest = resolveLink(n);
    if (dest) {
      setOpen(false);
      navigate(dest);
    }
  };

  return (
    <div className="relative">
      <button
        ref={buttonRef}
        onClick={() => { setOpen((v) => !v); if (!open) fetchItems(); }}
        className="relative p-2 rounded-xl hover:bg-white/5 transition-colors"
        style={{ color: '#A0AEC0' }}
        data-testid="notification-bell"
        aria-label="Notifications"
      >
        <Bell className="w-5 h-5" strokeWidth={1.5} />
        {unread > 0 && (
          <span
            className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] rounded-full flex items-center justify-center text-[10px] font-bold"
            style={{ background: '#EF4444', color: '#fff', padding: '0 4px' }}
            data-testid="notification-badge"
          >
            {unread > 9 ? '9+' : unread}
          </span>
        )}
      </button>

      {open && (
        <div
          ref={popoverRef}
          className="absolute right-0 mt-2 w-[380px] max-w-[95vw] rounded-xl z-50"
          style={{
            background: '#0F1923',
            border: '1px solid #2D3748',
            boxShadow: '0 20px 40px rgba(0,0,0,0.4)',
          }}
          data-testid="notification-drawer"
        >
          <div
            className="flex items-center justify-between px-4 py-3"
            style={{ borderBottom: '1px solid #2D3748' }}
          >
            <div className="flex items-center gap-2">
              <Bell className="w-4 h-4" style={{ color: '#1E88E5' }} />
              <h3 className="text-sm font-semibold text-white">Notifications</h3>
              {unread > 0 && (
                <span className="text-[10px] px-1.5 py-0.5 rounded-full"
                      style={{ background: 'rgba(239,68,68,0.15)', color: '#EF4444' }}>
                  {unread} unread
                </span>
              )}
            </div>
            <div className="flex items-center gap-1">
              <button
                onClick={() => setOpen(false)}
                className="p-1 rounded-md hover:bg-white/5"
                style={{ color: '#64748B' }}
                aria-label="Close"
              >
                <XIcon className="w-4 h-4" />
              </button>
            </div>
          </div>

          <div className="max-h-[440px] overflow-y-auto">
            {loading && items.length === 0 ? (
              <div className="p-6 text-center text-sm" style={{ color: '#64748B' }}>Loading…</div>
            ) : items.length === 0 ? (
              <div className="p-10 text-center">
                <div
                  className="w-14 h-14 rounded-2xl mx-auto mb-4 flex items-center justify-center"
                  style={{ background: 'rgba(30,136,229,0.1)' }}
                >
                  <Bell className="w-7 h-7 text-blue-400" />
                </div>
                <p className="text-sm font-medium text-white mb-1">You're all caught up</p>
                <p className="text-xs leading-relaxed" style={{ color: '#64748B' }}>
                  Job assignments, alerts, approvals, and system events will appear here.
                </p>
              </div>
            ) : (
              <ul>
                {items.map((n) => {
                  const Icon = ICON_BY_TYPE[n.type] || Info;
                  const col = COLOR_BY_SEVERITY[n.severity] || '#A0AEC0';
                  const dest = resolveLink(n);
                  const isClickable = !!dest;
                  return (
                    <li
                      key={n.id}
                      className={`px-4 py-3 flex gap-3 transition-all group ${isClickable ? 'cursor-pointer hover:bg-white/[0.05] active:bg-white/10' : ''}`}
                      style={{
                        borderBottom: '1px solid #1A2433',
                        background: n.read ? 'transparent' : 'rgba(30,136,229,0.05)',
                      }}
                      onClick={() => onItemClick(n)}
                      data-testid={`notification-item-${n.id}`}
                    >
                      {/* Icon */}
                      <div
                        className="flex-shrink-0 w-9 h-9 rounded-full flex items-center justify-center mt-0.5"
                        style={{ background: `${col}20`, color: col }}
                      >
                        <Icon className="w-4 h-4" />
                      </div>

                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        {/* Title row */}
                        <div className="flex items-start justify-between gap-2">
                          <p className={`text-sm font-medium leading-snug ${n.read ? 'text-slate-300' : 'text-white'}`}>
                            {n.title}
                          </p>
                          <div className="flex items-center gap-1.5 flex-shrink-0 mt-0.5">
                            {!n.read && (
                              <span className="w-2 h-2 rounded-full bg-blue-500" aria-label="unread" />
                            )}
                            {isClickable && (
                              <ChevronRight className="w-3.5 h-3.5 text-slate-600 opacity-0 group-hover:opacity-100 transition-opacity" />
                            )}
                          </div>
                        </div>

                        {/* Body */}
                        {(n.body || n.message) && (
                          <p className="text-xs mt-0.5 leading-relaxed line-clamp-2" style={{ color: '#94A3B8' }}>
                            {n.body || n.message}
                          </p>
                        )}

                        {/* Action row */}
                        <div className="flex items-center justify-between mt-2 gap-2">
                          <span className="text-[11px]" style={{ color: '#64748B' }}>
                            {timeAgo(n.created_at)}
                          </span>
                          <div className="flex items-center gap-2">
                            {/* Accept Job button — job assignments only */}
                            {ACK_TYPES.has(n.type) && n?.meta?.job_id && !n.acknowledged && (
                              <button
                                onClick={(e) => { e.stopPropagation(); acknowledgeAssignment(n); }}
                                disabled={ackingId === n.id}
                                className="text-[11px] px-2.5 py-1 rounded-lg flex items-center gap-1 font-semibold transition-all disabled:opacity-50 active:scale-95"
                                style={{
                                  background: 'rgba(0,200,150,0.15)',
                                  color: '#00C896',
                                  border: '1px solid rgba(0,200,150,0.35)',
                                }}
                                data-testid={`ack-assignment-${n.id}`}
                              >
                                <Check className="w-3 h-3" />
                                {ackingId === n.id ? 'Accepting…' : 'Accept Job'}
                              </button>
                            )}

                            {/* Accepted confirmation */}
                            {ACK_TYPES.has(n.type) && n.acknowledged && (
                              <span
                                className="text-[11px] px-2 py-1 rounded-lg flex items-center gap-1 font-medium"
                                style={{ background: 'rgba(0,200,150,0.10)', color: '#00C896' }}
                                data-testid={`ack-assignment-done-${n.id}`}
                              >
                                <Check className="w-3 h-3" /> Accepted
                              </span>
                            )}

                            {/* View → button for non-job-assignment clickable notifications */}
                            {isClickable && !ACK_TYPES.has(n.type) && (
                              <button
                                onClick={(e) => { e.stopPropagation(); onItemClick(n); }}
                                className="text-[11px] px-2.5 py-1 rounded-lg flex items-center gap-1 font-medium transition-all hover:bg-white/10 active:scale-95"
                                style={{ color: '#60A5FA' }}
                                data-testid={`notification-view-${n.id}`}
                              >
                                View →
                              </button>
                            )}

                            {/* Dismiss */}
                            <button
                              onClick={(e) => { e.stopPropagation(); del(n.id); }}
                              className="text-[11px] p-1.5 rounded-lg transition-all hover:bg-red-500/10 hover:text-red-400 active:scale-95"
                              style={{ color: '#475569' }}
                              data-testid={`delete-notification-${n.id}`}
                              title="Dismiss"
                            >
                              <XIcon className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
            {items.length > 0 && (
              <div
                className="px-4 py-3 flex items-center justify-between"
                style={{ borderTop: '1px solid #2D3748' }}
              >
                <button
                  onClick={markAll}
                  className="text-xs flex items-center gap-1 hover:text-slate-300 transition-colors"
                  style={{ color: '#475569' }}
                  data-testid="mark-all-read-btn"
                >
                  <CheckCheck className="w-3 h-3" /> Mark all read
                </button>
                <button
                  onClick={() => { setOpen(false); navigate('/reports'); }}
                  className="text-xs text-blue-400 hover:text-blue-300 transition-colors font-medium"
                  data-testid="notifications-view-all-activity"
                >
                  View all activity →
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
